import * as React from 'react';

/** Material UI Packages. */
import { Typography } from '@mui/material';

/** Material UI Icons. */
import HistoryIcon from '@mui/icons-material/History';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import HeaderWithIcon from './HeaderIcon';
import { formatOperationDateTime, formatToMMDDYY } from 'shared/lib/dateFormatter';

/**
 * Create a tooltip for a single history item.
 * @param {Object} historyData - The history data.
 * @param {number} tabValue - The current tab value.
 * @param {Object} tab - The current tab.
 * @param {Array} attributes - The attributes to include in the tooltip.
 * @returns {string} The formatted tooltip.
 */
export const createHistoryTooltip = (historyData, tabValue, tab, attributes) => {
    const matchedHistory =
        historyData[tabValue]?.length > 0
            ? historyData[tabValue]
                  .filter((item) => item.table_id === tab.id && attributes.includes(item.attribute))
                  .sort((a, b) => a.id - b.id)
            : [];

    // Append the history details: date, value, and registered_by information
    return matchedHistory.length > 0
        ? matchedHistory
              .map(
                  (item) =>
                      `${formatOperationDateTime(item.modified_on, 'M/D/YYYY')}: ${formatToMMDDYY(item.value)} ${
                          item.registered_by
                      }`
              )
              .join('<br />')
        : '';
};

/**
 * Create a header group for a single history item. e.g) house table
 * @param {Object} params - The params object.
 * @param {Object} historyData - The history data.
 * @param {number} tabValue - The current tab value.
 * @param {Object} tab - The current tab.
 * @param {Array} attributes - The attributes to include in the tooltip.
 * @returns {React.ReactElement} The header group.
 */
export const createHeaderGroup = (params, historyData, tabValue, tab, attributes) => {
    const formattedTooltip = createHistoryTooltip(historyData, tabValue, tab, attributes);
    return (
        <HeaderWithIcon
            {...params}
            icon={<HistoryIcon sx={customized_style.itemIcon} />}
            tooltipTitle={
                <Typography
                    dangerouslySetInnerHTML={{ __html: formattedTooltip }}
                    sx={{ fontSize: '9px', fontWeight: 'bold' }}
                />
            }
        />
    );
};

/**
 * Create a tooltip for multiple history items.
 * @param {Object} historyData - The history data.
 * @param {number} tabValue - The current tab value.
 * @param {Array} tabs - The current tabs.
 * @param {Array} attributes - The attributes to include in the tooltip.
 * @returns {string} The formatted tooltip.
 */
const createMultiHistoryTooltip = (historyData, tabValue, tabs, attributes) => {
    const matchedHistory =
        historyData[tabValue]?.length > 0
            ? historyData[tabValue]
                  .filter((item) => tabs.some((t) => t.id === item.table_id) && attributes.includes(item.attribute))
                  .sort((a, b) => a.table_id - b.table_id || a.id - b.id)
            : [];

    if (matchedHistory.length === 0) return '';

    let lastTableId = null; // Stores the last processed table_id to track changes
    let formattedTooltip = ''; // Final formatted HTML string

    matchedHistory.forEach((item, index) => {
        // Check if the current table_id is different from the last processed one.
        if (item.table_id !== lastTableId) {
            // If this is not the first group, add a line break to separate previous data.
            if (lastTableId !== null) {
                formattedTooltip += '<br />';
            }
            // Find the corresponding tab entry to get the cntr_number
            const tabItem = tabs.find((t) => t.id === item.table_id);

            if (tabItem) {
                // Use 'alias' if tabValue is 2 or 3, otherwise use 'cntr_number'
                const displayValue = tabValue === 0 || tabValue === 1 ? tabItem.cntr_number : tabItem.alias;

                // Add the selected value in bold before listing history items
                formattedTooltip += `<strong>${displayValue}</strong><br />`;
            }
            // Update lastTableId to track the current group
            lastTableId = item.table_id;
        }

        let displayValue = formatToMMDDYY(item.value);

        // Append the history details: date, value, and registered_by information
        formattedTooltip += `${formatOperationDateTime(item.modified_on, 'M/D/YYYY')}: ${displayValue} ${
            item.registered_by
        }`;

        // Add a line break after each entry
        formattedTooltip += '<br />';
    });

    return formattedTooltip;
};

/**
 * Create a header group for multiple history items. e.g) container, bol table
 * @param {Object} params - The params object.
 * @param {Object} historyData - The history data.
 * @param {number} tabValue - The current tab value.
 * @param {Object} tab - The current tab.
 * @param {Array} attributes - The attributes to include in the tooltip.
 * @returns {React.ReactElement} The header group.
 */
export const createMultiHeaderGroup = (params, historyData, tabValue, tab, attributes) => {
    const formattedTooltip = createMultiHistoryTooltip(historyData, tabValue, tab, attributes);

    return (
        <HeaderWithIcon
            {...params}
            icon={<HistoryIcon sx={customized_style.itemIcon} />}
            tooltipTitle={
                <Typography
                    dangerouslySetInnerHTML={{ __html: formattedTooltip }}
                    sx={{ fontSize: '9px', fontWeight: 'bold' }}
                />
            }
        />
    );
};
